const PENDING_QUOTE_REQUEST_KEY = 'etc_pending_quote_request';
const QUOTE_REQUEST_HISTORY_KEY = 'etc_quote_request_history';
const QUOTE_REQUEST_SCOPE_KEY = 'etc_quote_request_scope';
const QUOTE_REQUEST_EVENT = 'quote-request-updated';
const DEFAULT_QUOTE_REQUEST_SCOPE = 'guest';

function parseStoredJson(raw, fallback) {
  if (!raw) return fallback;
  try {
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch (err) {
    return fallback;
  }
}

function normalizeQuoteRequestScope(scope) {
  const raw = String(scope || '').trim();
  return raw || DEFAULT_QUOTE_REQUEST_SCOPE;
}

function normalizeRequest(request) {
  if (!request || typeof request !== 'object') return null;
  const requestNumber = String(request.requestNumber || '').trim();
  if (!requestNumber) return null;

  return {
    ...request,
    requestNumber,
    status: String(request.status || 'new').trim().toLowerCase() || 'new',
  };
}

function getScopedStorageKey(baseKey, scope) {
  return `${baseKey}:${normalizeQuoteRequestScope(scope)}`;
}

function readStoredArray(key, scope = getQuoteRequestScope()) {
  if (typeof window === 'undefined') return [];
  const parsed = parseStoredJson(localStorage.getItem(getScopedStorageKey(key, scope)), []);
  return Array.isArray(parsed) ? parsed : [];
}

function writeStoredValue(key, value, scope = getQuoteRequestScope()) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(getScopedStorageKey(key, scope), JSON.stringify(value));
  window.dispatchEvent(new Event(QUOTE_REQUEST_EVENT));
}

function resolveRequestScope(request) {
  return normalizeQuoteRequestScope(
    request?.userId ||
      request?.user?.id ||
      request?.customer?.userId ||
      request?.customerId ||
      getQuoteRequestScope(),
  );
}

function getLegacyPendingRequest() {
  if (typeof window === 'undefined') return null;
  return normalizeRequest(parseStoredJson(localStorage.getItem(PENDING_QUOTE_REQUEST_KEY), null));
}

function removeLegacyPendingRequest() {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(PENDING_QUOTE_REQUEST_KEY);
}

export function isTerminalQuoteRequestStatus(status = '') {
  const normalized = String(status || '').trim().toLowerCase();
  return ['quoted', 'sent', 'accepted', 'declined', 'refused', 'rejected', 'archived', 'deleted'].includes(normalized);
}

export function getQuoteRequestScope() {
  if (typeof window === 'undefined') return null;
  return normalizeQuoteRequestScope(localStorage.getItem(QUOTE_REQUEST_SCOPE_KEY));
}

export function setQuoteRequestScope(scope, options = {}) {
  if (typeof window === 'undefined') return DEFAULT_QUOTE_REQUEST_SCOPE;

  const nextScope = normalizeQuoteRequestScope(scope);
  const previousScope = getQuoteRequestScope();
  localStorage.setItem(QUOTE_REQUEST_SCOPE_KEY, nextScope);

  if (options?.adoptLegacy && nextScope !== DEFAULT_QUOTE_REQUEST_SCOPE) {
    const legacyPending = getLegacyPendingRequest();
    if (legacyPending && resolveRequestScope(legacyPending) === nextScope) {
      localStorage.setItem(
        getScopedStorageKey(PENDING_QUOTE_REQUEST_KEY, nextScope),
        JSON.stringify(legacyPending),
      );
      removeLegacyPendingRequest();
    }
  }

  if (previousScope !== nextScope) {
    window.dispatchEvent(new Event(QUOTE_REQUEST_EVENT));
  }

  return nextScope;
}

export function getPendingQuoteRequest() {
  if (typeof window === 'undefined') return null;
  const scope = getQuoteRequestScope();
  return normalizeRequest(parseStoredJson(localStorage.getItem(getScopedStorageKey(PENDING_QUOTE_REQUEST_KEY, scope)), null));
}

export function setPendingQuoteRequest(request) {
  const normalized = normalizeRequest(request);
  if (!normalized || typeof window === 'undefined') return null;
  const scope = resolveRequestScope(normalized);
  setQuoteRequestScope(scope, { adoptLegacy: true });
  writeStoredValue(PENDING_QUOTE_REQUEST_KEY, normalized, scope);
  return normalized;
}

export function clearPendingQuoteRequest() {
  if (typeof window === 'undefined') return;
  const scope = getQuoteRequestScope();
  localStorage.removeItem(getScopedStorageKey(PENDING_QUOTE_REQUEST_KEY, scope));
  window.dispatchEvent(new Event(QUOTE_REQUEST_EVENT));
}

export function getQuoteRequestHistory() {
  if (typeof window === 'undefined') return [];
  const scope = getQuoteRequestScope();
  return readStoredArray(QUOTE_REQUEST_HISTORY_KEY, scope).map(normalizeRequest).filter(Boolean);
}

export function saveQuoteRequestHistory(requests) {
  if (typeof window === 'undefined') return [];
  const normalized = Array.isArray(requests)
    ? requests.map(normalizeRequest).filter(Boolean)
    : [];
  writeStoredValue(QUOTE_REQUEST_HISTORY_KEY, normalized);
  return normalized;
}

export function archiveQuoteRequest(request) {
  const normalized = normalizeRequest(request);
  if (!normalized || typeof window === 'undefined') return [];
  const current = getQuoteRequestHistory();
  const next = [
    normalized,
    ...current.filter((item) => item.requestNumber !== normalized.requestNumber),
  ].slice(0, 20);
  saveQuoteRequestHistory(next);
  return next;
}

export function syncPendingQuoteRequestStatus(request) {
  const normalized = normalizeRequest(request);
  if (!normalized || typeof window === 'undefined') return null;
  if (isTerminalQuoteRequestStatus(normalized.status)) {
    archiveQuoteRequest(normalized);
    clearPendingQuoteRequest();
    return null;
  }
  setPendingQuoteRequest(normalized);
  return normalized;
}

export { QUOTE_REQUEST_EVENT, PENDING_QUOTE_REQUEST_KEY, QUOTE_REQUEST_HISTORY_KEY, QUOTE_REQUEST_SCOPE_KEY };
